/**
 * OSMDExerciseScene.takeRecord - an exercise take, graded through the grading engine and
 * recorded as a take record.
 *
 * Pure, Phaser-free. The scene buffers a take's key presses and releases on the sitting's
 * session clock (milliseconds since the sitting's first take opened) and hands them here when
 * the take ends. Recording the take is grading it: the recorder folds the events through the
 * engine under the Take Ladder preset, so the verdict the sitting counts and the stored record
 * are one computation, and replaying the stored record gives the same judged take.
 *
 * What a stored exercise take holds:
 *   - the realized notes the take was graded against (the material), with the material key
 *     (package key, level, tier) as the score's `source`;
 *   - every key press and release, at full precision (the raw keyboard input);
 *   - the stamp: the cell (exercise, scale, tonic), the hands, the range / direction / rhythm,
 *     the tempo, the click (metronome) when it was on, the preset and the engine version;
 *   - the setup the take was graded under, including the full scoring rule (the Take Ladder),
 *     so the verdict can be re-derived under the rule in force when it was played, and how the
 *     run was written (`display.keySignature`: under its key signature or accidentals only, a
 *     display fact that is never graded and is not an assist);
 *   - when the take opened and ended on the session clock (the session row holds the wall-clock
 *     start), and how it ended: `finished` (the music ended), `stopped` or `left`.
 * A take with nothing played stores no events: its event count of 0 is the nothing-played
 * marker.
 */

import {
  ClockSpec,
  GradingConfigError,
  HandRole,
  KeySignatureDisplay,
  JudgedTake,
  PerformanceEvent,
  RealizedScore,
  RecorderState,
  ScoreNote,
  ScoringConfig,
  TAKE_LADDER,
  TakeEndReason,
  addMaterial,
  beginTake,
  endTake,
  recordEvent,
  recordSetting,
  scoreTake,
  setTempoSpec,
  startSession,
  type Hand as GradedHand,
} from 'Grading';
import TimeSignature from 'Models/TimeSignature';
import { tempoUnitInBottomNotes } from 'Models/MeterTiming';
import type { ScaleCell, ScaleConfig, Verdict } from 'Types/ExerciseContentTypes';
import type { JudgedNote } from './OSMDExerciseScene.expander';
import type { BandBreakdown, GradeComputation } from './OSMDExerciseScene.grader';

/** The preset an exercise take is graded under. */
export const EXERCISE_PRESET = TAKE_LADDER;

/** Which content a take's notes were realized from. */
export interface ExerciseMaterialKey {
  readonly packageKey: string;
  readonly levelNumber: number;
  readonly tierNumber: number;
}

/** The expander's division space: 4 divisions per quarter note (the engine's beat). */
const DIVISIONS_PER_QUARTER = 4;
const DIVISIONS_PER_WHOLE_NOTE = 16;
const MS_PER_MINUTE = 60000;

/**
 * The realized score of a run: one score note per sounding pitch, in quarter-note beats, each
 * labelled with its measure (from 1), plus the material key as its source when known.
 */
export function exerciseScore(
  judgedNotes: readonly JudgedNote[],
  timeSignature: readonly [number, number],
  materialKey: ExerciseMaterialKey | null,
): RealizedScore {
  const [beats, beatType] = timeSignature;
  const measureDivisions = (beats / beatType) * DIVISIONS_PER_WHOLE_NOTE;
  const notes: ScoreNote[] = judgedNotes.flatMap((note) =>
    note.midiNotes.map((pitch, voice) => ({
      id: note.midiNotes.length === 1 ? `n${note.index}` : `n${note.index}.${voice}`,
      pitch,
      onsetBeat: note.startDivisions / DIVISIONS_PER_QUARTER,
      offsetBeat: (note.startDivisions + note.durationDivisions) / DIVISIONS_PER_QUARTER,
      hand: note.hand === 'RH' ? 'right' : 'left',
      groups: { measure: String(Math.floor(note.startDivisions / measureDivisions) + 1) },
    })),
  );
  return materialKey
    ? {
        notes,
        source: {
          packageKey: materialKey.packageKey,
          levelNumber: materialKey.levelNumber,
          tierNumber: materialKey.tierNumber,
        },
      }
    : { notes };
}

/** The hands lever for an exercise hand: the hands played are graded; the other is silent. */
export function exerciseHands(hand: ScaleConfig['hand']): Record<GradedHand, HandRole> {
  return { left: hand === 'RH' ? 'silent' : 'you', right: hand === 'LH' ? 'silent' : 'you' };
}

/** Quarter notes per minute for a tempo counted in the meter's tempo unit (a quarter in x/4). */
export function quarterNoteBpm(tempo: number, timeSignature: readonly [number, number]): number {
  const [numerator, denominator] = timeSignature;
  const unitInBottomNotes = tempoUnitInBottomNotes(new TimeSignature(numerator, denominator));
  return (tempo * unitInBottomNotes * DIVISIONS_PER_WHOLE_NOTE) / (denominator * 4);
}

export interface ExerciseRecordStart {
  /** Generated on the device: the take session's id. */
  readonly sessionId: string;
  /** Wall-clock time at session clock 0 (ISO 8601). */
  readonly startedAt: string;
  readonly appVersion: string | null;
  readonly materialId: string;
  readonly score: RealizedScore;
}

/** A sitting's record, holding the run's realized notes from session clock 0. */
export function startExerciseRecord(start: ExerciseRecordStart): RecorderState {
  const session = startSession({
    sessionId: start.sessionId,
    surface: 'exercise',
    startedAt: start.startedAt,
    appVersion: start.appVersion,
  });
  return addMaterial(session, 0, { materialId: start.materialId, score: start.score });
}

/** One take, as the scene saw it. Times are on the session clock. */
export interface ExerciseTake {
  readonly takeId: string;
  readonly materialId: string;
  /** When the take's response window opened (Start or Next). */
  readonly openedAtMs: number;
  /**
   * When beat 0 fell due: the transport's start, which the take's first note sets off. Null
   * when the transport never started (nothing was played).
   */
  readonly beatZeroMs: number | null;
  /** The key presses and releases of the take, in time order. */
  readonly events: readonly PerformanceEvent[];
  readonly endedAtMs: number;
  readonly reason: TakeEndReason;
  readonly exerciseId: number;
  readonly cell: ScaleCell;
  readonly config: ScaleConfig;
  readonly timeSignature: readonly [number, number];
  /** The metronome clicked during the take (an assist). */
  readonly clickOn: boolean;
  /**
   * How the run was written: under its key signature or accidentals only. A display fact, not an
   * assist: stored with the take's setup (`display.keySignature`), never graded, never stamped.
   */
  readonly keySignatureDisplay?: KeySignatureDisplay;
}

/** The take's clock: its set tempo from beat 0; a take that never started puts beat 0 after it. */
function takeClock(take: ExerciseTake): ClockSpec {
  const bpm = quarterNoteBpm(take.config.tempo, take.timeSignature);
  return take.beatZeroMs !== null
    ? setTempoSpec(bpm, take.beatZeroMs)
    : setTempoSpec(bpm, take.endedAtMs + MS_PER_MINUTE / bpm);
}

/** The click in force for the take, recorded only when it changes. */
function withClick(state: RecorderState, take: ExerciseTake): RecorderState {
  const level = take.clickOn ? 1 : 0;
  return (state.inForce.assists.click ?? 0) === level
    ? state
    : recordSetting(state, take.openedAtMs, { name: 'assist', assist: 'click', level });
}

/**
 * Records one ended take: it is graded as it is recorded. Throws {@link GradingConfigError}
 * when the take does not fit the record (events out of time order, an invalid cell).
 */
export function recordExerciseTake(state: RecorderState, take: ExerciseTake): RecorderState {
  const bpm = quarterNoteBpm(take.config.tempo, take.timeSignature);
  const begun = beginTake(withClick(state, take), take.openedAtMs, {
    takeId: take.takeId,
    materialId: take.materialId,
    judgment: EXERCISE_PRESET.judgment,
    scoring: EXERCISE_PRESET.scoring,
    clock: takeClock(take),
    referenceTempoBpm: bpm,
    context: {
      cell: {
        surface: 'exercise',
        exerciseId: take.exerciseId,
        scale: take.cell.scale,
        tonic: take.cell.tonic,
      },
      hands: exerciseHands(take.config.hand),
      exercise: {
        rangeOctaves: take.config.range_octaves,
        direction: take.config.direction,
        rhythmDensity: take.config.rhythm_density,
      },
      preset: EXERCISE_PRESET.id,
      input: { mode: 'midi', latencyMs: null },
    },
    ...(take.keySignatureDisplay ? { display: { keySignature: take.keySignatureDisplay } } : {}),
  });
  const played = take.events.reduce(recordEvent, begun);
  return endTake(played, take.endedAtMs, { reason: take.reason });
}

const VERDICTS: readonly Verdict[] = ['failed', 'clean', 'flawless'];

const isVerdict = (label: string | null | undefined): label is Verdict =>
  VERDICTS.indexOf(label as Verdict) >= 0;

/**
 * The take's grade from its judged take: the band counts and the verdict the Take Ladder
 * rules (recorded with the take, so a replay rules the same), and the green fraction as a
 * readout. `pressed` is false for a take with no key pressed: it is flagged no-input, which
 * never counts.
 */
export function exerciseTakeGrade(
  judged: JudgedTake,
  scoring: ScoringConfig = EXERCISE_PRESET.scoring,
  pressed = true,
): GradeComputation {
  const scored = scoreTake(judged, scoring);
  const tally = scored.views.combined.latest?.tally ?? {};
  const breakdown: BandBreakdown = {
    green: tally.green ?? 0,
    yellow: tally.yellow ?? 0,
    red: tally.red ?? 0,
    total: (tally.green ?? 0) + (tally.yellow ?? 0) + (tally.red ?? 0),
  };
  const label = scored.verdicts[0]?.label;
  if (!isVerdict(label)) {
    throw new GradingConfigError(`the exercise verdict is one of ${VERDICTS.join(', ')}`);
  }
  return {
    breakdown,
    anyRed: breakdown.red > 0,
    allGreen: breakdown.total > 0 && breakdown.red === 0 && breakdown.yellow === 0,
    verdict: label,
    accuracy: breakdown.total > 0 ? Math.round((breakdown.green / breakdown.total) * 100) : 0,
    noInput: !pressed,
  };
}

/** True when the take pressed at least one key. */
export const anyKeyPressed = (events: readonly PerformanceEvent[]): boolean =>
  events.some((event) => event.kind === 'press');
